import { useEffect, useState } from "react";

type useRefElementScrollbarWidthProps = {
	containerRef: React.RefObject<HTMLElement>;
	additionalTrigger?: number;
};

export const useRefElementScrollbarWidth = ({
	containerRef,
	additionalTrigger,
}: useRefElementScrollbarWidthProps) => {
	const [scrollInfo, setScrollInfo] = useState({
		isVerticalScroll: false,
		isHorizontalScroll: false,
		verticalScrollbarWidth: 0,
		horizontalScrollbarWidth: 0,
	});

	useEffect(() => {
		if (
			typeof ResizeObserver !== "function" ||
			typeof ResizeObserver === "undefined" ||
			!containerRef?.current
		) {
			return;
		}
		const container = containerRef.current;

		const handleResize = () => {
			const newHasVerticalScroll =
				container.scrollHeight > container.clientHeight;
			const newHasHorizontalScroll =
				container.scrollWidth > container.clientWidth;
			const newVerticalScrollWidth = newHasVerticalScroll
				? container.offsetWidth - container.clientWidth
				: 0;
			const newHorizontalScrollWidth = newHasHorizontalScroll
				? container.offsetHeight - container.clientHeight
				: 0;

			if (
				newHasVerticalScroll !== scrollInfo.isVerticalScroll ||
				newHasHorizontalScroll !== scrollInfo.isHorizontalScroll ||
				newVerticalScrollWidth !== scrollInfo.verticalScrollbarWidth ||
				newHorizontalScrollWidth !== scrollInfo.horizontalScrollbarWidth
			) {
				setScrollInfo({
					isVerticalScroll: newHasVerticalScroll,
					isHorizontalScroll: newHasHorizontalScroll,
					verticalScrollbarWidth: newVerticalScrollWidth,
					horizontalScrollbarWidth: newHorizontalScrollWidth,
				});
			}
		};

		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, [
		containerRef.current,
		additionalTrigger,
		scrollInfo.isVerticalScroll,
		scrollInfo.verticalScrollbarWidth,
		scrollInfo.isHorizontalScroll,
		scrollInfo.horizontalScrollbarWidth,
	]);

	return scrollInfo;
};

export default useRefElementScrollbarWidth;
